import React, { useEffect, useState } from 'react';
import PageBanner from '../components/PageBanner';
import api from '../utils/api';

// Fallback shown when the API isn't reachable yet (fresh install, dev without backend, etc.).
// The shape matches what /api/books.php returns: links[] of { store, url }.
const FALLBACK_BOOKS = [
  {
    id: 'fallback-1',
    title: 'Psychology of Self-Talk',
    subtitle: '7 Mindfulness Mantras for a Happier, Healthier and Successful You',
    author: 'Prof. Manju Agrawal',
    image: '/assets/books/book-psychology-self-talk.webp',
    description:
      'A transformative guide from Professor Emeritus Dr. Manju Agrawal exploring how your inner dialogue shapes your mental health, relationships, and success. Drawing on 40+ years of clinical and teaching experience in psychology and hypnotherapy, this book offers 7 practical mantras for rewiring self-talk.',
    price_inr: 499,
    pages: 240,
    links: [],
  },
];

const STORE_COLORS = {
  Amazon:   { bg: '#FF9900', text: '#0F1111' },
  Flipkart: { bg: '#FFC200', text: '#172337' },
};

function buyButtonStyle(store) {
  const c = STORE_COLORS[store];
  return {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    padding: '9px 16px',
    borderRadius: 999,
    fontSize: 13.5,
    fontWeight: 600,
    textDecoration: 'none',
    background: c ? c.bg : 'var(--accent)',
    color: c ? c.text : '#fff',
    transition: 'transform 0.2s ease, box-shadow 0.2s ease',
  };
}

const styles = {
  section: { padding: '60px 0 80px', background: 'var(--bg-white)' },
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))',
    gap: '28px',
    marginTop: '40px',
    maxWidth: 900,
    margin: '40px auto 0',
  },
  card: {
    background: 'var(--bg-white)',
    border: '1px solid var(--border-light)',
    borderRadius: 'var(--radius-md)',
    overflow: 'hidden',
    transition: 'transform 0.3s ease, box-shadow 0.3s ease',
  },
  cover: {
    width: '100%',
    height: 400,
    objectFit: 'cover',
    objectPosition: 'center',
    background: 'var(--bg-light)',
  },
  body: { padding: 20 },
  title: {
    fontFamily: 'var(--font-heading)',
    fontSize: 18,
    fontWeight: 500,
    color: 'var(--text-heading)',
    marginBottom: 4,
    lineHeight: 1.3,
  },
  author: {
    fontSize: 12,
    color: 'var(--accent)',
    fontWeight: 600,
    marginBottom: 12,
  },
  desc: {
    fontSize: 13,
    color: 'var(--text-body)',
    lineHeight: 1.6,
    marginBottom: 16,
  },
  meta: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingTop: 12,
    borderTop: '1px solid var(--border-light)',
    marginBottom: 14,
  },
  price: {
    fontFamily: 'var(--font-heading)',
    fontSize: 20,
    fontWeight: 500,
    color: 'var(--text-heading)',
  },
  pages: { fontSize: 12, color: 'var(--text-light)' },
  buyRow: { display: 'flex', flexWrap: 'wrap', gap: 8 },
  empty: { textAlign: 'center', padding: '60px 20px', color: 'var(--text-body)' },
};

function BookCard({ book }) {
  return (
    <div
      style={styles.card}
      onMouseEnter={(e) => {
        e.currentTarget.style.transform = 'translateY(-4px)';
        e.currentTarget.style.boxShadow = 'var(--shadow-lg)';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.transform = 'translateY(0)';
        e.currentTarget.style.boxShadow = 'none';
      }}
    >
      {book.image && <img src={book.image} alt={book.title} style={styles.cover} />}
      <div style={styles.body}>
        <h3 style={styles.title}>{book.title}</h3>
        {book.subtitle && (
          <p style={{ fontSize: 13, fontStyle: 'italic', color: 'var(--text-light)', marginBottom: 8, lineHeight: 1.4 }}>
            {book.subtitle}
          </p>
        )}
        {book.author && <p style={styles.author}>by {book.author}</p>}
        {book.description && (
          <div
            style={styles.desc}
            dangerouslySetInnerHTML={{ __html: book.description }}
          />
        )}
        <div style={styles.meta}>
          {Number(book.price_inr) > 0 && (
            <span style={styles.price}>₹{Number(book.price_inr).toLocaleString('en-IN')}</span>
          )}
          {book.pages && <span style={styles.pages}>{book.pages} pages</span>}
        </div>

        {Array.isArray(book.links) && book.links.length > 0 ? (
          <div style={styles.buyRow}>
            {book.links.map((l, i) => (
              <a
                key={`${l.store}-${i}`}
                href={l.url}
                target="_blank"
                rel="noopener noreferrer"
                style={buyButtonStyle(l.store)}
                onMouseEnter={(e) => { e.currentTarget.style.transform = 'translateY(-1px)'; }}
                onMouseLeave={(e) => { e.currentTarget.style.transform = 'translateY(0)'; }}
              >
                Buy on {l.store}
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                  <path d="M7 17L17 7M9 7h8v8" />
                </svg>
              </a>
            ))}
          </div>
        ) : (
          <a href="/contact" className="btn-outline" style={{ width: '100%', textAlign: 'center', display: 'block' }}>
            Contact to order
          </a>
        )}
      </div>
    </div>
  );
}

function BooksPage() {
  const [books, setBooks] = useState(null); // null = loading
  const [usingFallback, setUsingFallback] = useState(false);

  useEffect(() => {
    let active = true;
    api.getBooks()
      .then((data) => {
        if (!active) return;
        if (Array.isArray(data) && data.length > 0) {
          setBooks(data);
          setUsingFallback(false);
        } else {
          // API reachable but empty — show empty state, not fallback.
          setBooks([]);
        }
      })
      .catch(() => {
        if (!active) return;
        setBooks(FALLBACK_BOOKS);
        setUsingFallback(true);
      });
    return () => { active = false; };
  }, []);

  return (
    <>
      <PageBanner title="Books" />

      <section style={styles.section}>
        <div className="container">
          <h2 className="section-title">Books by MindSpa</h2>
          <p className="section-subtitle">
            Evidence-based reads on psychology, therapy &amp; personal growth
          </p>
          <div className="section-divider">
            <span className="line"></span>
            <span className="dot"></span>
            <span className="dot"></span>
            <span className="dot"></span>
            <span className="line"></span>
          </div>

          {books === null ? (
            <div style={styles.empty}>Loading books…</div>
          ) : books.length === 0 ? (
            <div style={styles.empty}>
              <p>No books published yet — check back soon.</p>
            </div>
          ) : (
            <>
              <div style={styles.grid}>
                {books.map((b) => <BookCard key={b.id ?? b.slug ?? b.title} book={b} />)}
              </div>

              {usingFallback && (
                <div style={{ textAlign: 'center', marginTop: 50 }}>
                  <p style={{ fontSize: 15, color: 'var(--text-body)', marginBottom: 20 }}>
                    Books are available as hardcover and ebook. Contact us to order.
                  </p>
                  <a href="/contact" className="btn-primary">Contact to Order</a>
                </div>
              )}
            </>
          )}
        </div>
      </section>
    </>
  );
}

export default BooksPage;
